import { Box, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useActions } from '../../../hooks/useActions';
import { useForm } from '../../../lib/components';
import { TooltipComponent } from '../../../lib/components/atomic/feedback';
import { ArrowForwardIconSvg, InfoIcon, SaveIcon } from '../../../lib/icons';
import { useStepper } from '../partials/actionStepperPartial';
import { BenefitBudgetForm } from './form';
import { EmployeeTable } from './table';
import { ApiError } from '@pxtra-client-lib-legacy/gen';

const BACK_TO_PATH = '/employees';

export const UpdateBenefitBudget: FC = () => {
  const { formRef, handleSubmit } = useForm();
  const {
    activeStep,
    setTitle,
    setSectionTitle,
    setBackToPath,
    setBackToTitle,
    setStepperConfig,
    stepperConfig,
    setNextButtonConfig
  } = useStepper();
  const {
    selectedEmployees,
    setSelectedEmployees,
    selectedUsers,
    setSelectedUsers,
    setBulkUpdateType,
    setBulkUpdateValue,
    setBulkUpdateDate
  } = useActions();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>();

  // Return to Overview Page if no users are selected
  useEffect(() => {
    if (!selectedEmployees || Object.keys(selectedEmployees).length === 0) {
      navigate(BACK_TO_PATH);
    } else {
      setSelectedUsers(Object.keys(selectedEmployees));
    }
  }, []);

  // Initialize Wrapper values
  useEffect(() => {
    setTitle(t('form:EDIT_BENEFIT_BUDGET'));
    setBackToPath(BACK_TO_PATH);
    setBackToTitle(t('account:BACK_TO_OVERVIEW'));
    setStepperConfig([
      {
        title: t('form:DEFINE_BUDGET', { count: selectedUsers?.length ?? 0 })
      },
      {
        title: t('form:SUMMARY')
      }
    ]);
    setBulkUpdateType('FIXED_VALUE');
  }, [selectedUsers]);

  // control form steps
  useEffect(() => {
    if (activeStep === 3) {
      handleSubmit();
    } else if (activeStep === 2) {
      formRef.current?.validateForm().then((values) => {
        for (const key in values) {
          formRef.current?.setFieldError(key, values[key] as string);
          formRef.current?.setFieldTouched(key);
        }
        if (Object.keys(values).length > 0) {
          navigate(-1);
        }
      });
    }
    if (activeStep) {
      setSectionTitle(
        stepperConfig && stepperConfig[activeStep - 1] && stepperConfig[activeStep - 1].title
      );
    }

    if (activeStep && activeStep === 2) {
      setNextButtonConfig({
        text: t('common:SAVE'),
        startIcon: <SaveIcon />,
        endIcon: undefined
      });
    } else {
      setNextButtonConfig({
        text: t('common:NEXT'),
        startIcon: undefined,
        endIcon: <ArrowForwardIconSvg />
      });
    }
  }, [activeStep, stepperConfig]);

  const handleError = (error: unknown) => {
    setError(typeof error === 'string' ? error : JSON.stringify(error));
  };

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      sx={{
        '& .pxtra-bold': {
          fontWeight: 'bold'
        }
      }}
    >
      <Typography paragraph>
        {activeStep === 1
          ? t('form:CHOOSE_BENEFIT_UPDATE_METHOD')
          : t('form:BUDGET_UPDATE_SUMMARY')}
      </Typography>
      <Box
        sx={{
          '& form > .MuiGrid-root': {
            display: activeStep === 1 ? 'flex' : 'none'
          }
        }}
      >
        <BenefitBudgetForm
          formRef={formRef}
          selectedEmployeeIds={selectedUsers as string[]}
          onValueChanged={(value) => {
            setBulkUpdateValue(value);
            setError('');
          }}
          onTypeChanged={(type?: string) => {
            setBulkUpdateType(type);
            setBulkUpdateValue(undefined);
          }}
          onDateChanged={(value) => {
            setBulkUpdateDate(value);
            setError('');
          }}
          additionalSubmit={() => {
            navigate(BACK_TO_PATH);
            setSelectedEmployees({});
          }}
          onSubmitFailed={(error) => {
            navigate(-2);
            if (error instanceof ApiError) {
              let message = '';
              if ('body' in error && error.body) {
                message =
                  error.body.errors?.non_field_errors?.[0] ??
                  error.body.non_field_errors?.[0] ??
                  error.body.message ??
                  '';
              }
              handleError(message);
            } else {
              handleError(error);
            }
          }}
          useCustomButton
        />
      </Box>
      <Box sx={{ display: 'flex' }}>
        <TooltipComponent title={t('form:BUDGET_UPDATE_TOOLTIP')} placement="right" arrow>
          <Box paddingLeft="8px" paddingTop="0.7rem" paddingRight="1rem" maxWidth="fit-content">
            <InfoIcon height={28} width={18} color={error ? 'error.main' : 'text.secondary'} />
          </Box>
        </TooltipComponent>
        <Typography
          variant="body2"
          paddingTop="1rem"
          color={error ? 'error.main' : 'text.secondary'}
        >
          {error ? t('taskStatus:ERROR_one') + ': ' + error : ''}
        </Typography>
      </Box>
      <EmployeeTable />
    </Box>
  );
};
